import { useEffect } from "react";

import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";

import useAccountContacts from "../../data/useAccountContacts";
import useSalesQuotes from "../../data/useSalesQuotes";
import { IAccount } from "../../models/account/IAccount";
import { ICreateSalesQuoteRequest } from "../../models/SalesQuote";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { TextInput } from "../shared/Inputs/TextInput";

type ContactFields = Pick<ICreateSalesQuoteRequest, "contactName" | "contactEmail" | "contactPhone">;

interface SaveAsQuoteModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAccount?: IAccount;
  /** Everything except the contact fields - the basket already knows its own form state. */
  buildBody: () => Omit<ICreateSalesQuoteRequest, keyof ContactFields>;
  onSaved: () => void;
}

/**
 * Captures who the quote is for, then saves the basket as a sales quote. The account's primary
 * contact pre-fills the fields when one is selected; a walk-in just gets typed in.
 */
export default function SaveAsQuoteModal({ open, setOpen, selectedAccount, buildBody, onSaved }: SaveAsQuoteModalProps) {
  const { register, watch, reset } = useForm<ContactFields>({
    defaultValues: { contactName: "", contactEmail: "", contactPhone: "" },
  });
  const { accountContacts } = useAccountContacts(selectedAccount?.id, "SaveAsQuoteModal");
  const { create } = useSalesQuotes(undefined, "SaveAsQuoteModal");
  const { addNotification } = useNotification();

  const contactName = watch("contactName");

  useEffect(() => {
    if (!open) return;

    const contacts = accountContacts.data?.data ?? [];
    const primary = contacts.find((c) => c.id === selectedAccount?.primaryContactId) ?? contacts[0];

    reset({
      contactName: primary?.fullname || selectedAccount?.name || "",
      contactEmail: primary?.email || "",
      contactPhone: primary?.telephone1 || "",
    });
  }, [open, selectedAccount?.id, accountContacts.data]);

  const canSave = !!selectedAccount || !!contactName?.trim();

  const handleConfirm = () => {
    const contact = watch();
    const body: ICreateSalesQuoteRequest = {
      ...buildBody(),
      contactName: contact.contactName?.trim() || undefined,
      contactEmail: contact.contactEmail?.trim() || undefined,
      contactPhone: contact.contactPhone?.trim() || undefined,
    };

    create.mutate(body, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Quote saved",
          secondaryText: "Your basket has been cleared. Find it under Accounts → Sales Quotes.",
        });
        setOpen(false);
        onSaved();
      },
    });
  };

  return (
    <ConfirmModal
      open={open}
      setOpen={setOpen}
      title="Save basket as a quote?"
      message="The items and prices are kept on the quote and your basket is cleared. Nothing is sold or reserved."
      confirmButtonText="Save Quote"
      onConfirm={handleConfirm}
      isLoading={create.isLoading}
      confirmDisabled={!canSave}
      Icon={<DocumentDuplicateIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />}
      iconBg="bg-primary-100"
    >
      <div className="mt-4 flex flex-col gap-3 text-left">
        {selectedAccount && (
          <p className="text-sm text-gray-600">
            Account: <span className="font-medium text-gray-900">{selectedAccount.name}</span>
          </p>
        )}
        <TextInput label="Contact name" register={register} registerName="contactName" placeholder="Who is this quote for?" />
        <TextInput label="Email" register={register} registerName="contactEmail" type="email" />
        <TextInput label="Phone" register={register} registerName="contactPhone" type="tel" />
        {!canSave && <p className="text-xs text-amber-600">Enter a contact name or pick an account first.</p>}
      </div>
    </ConfirmModal>
  );
}
